import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Icon, SectionTabs } from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { useScope, ROLE_OPTIONS } from '@/lib/scope';
import { useAuth } from '@/lib/auth';
import { useOnboardingState } from '@/lib/onboarding';
import { getOnboardingFor } from '@/content/onboarding';
import { getHelpTasksFor, HELP_DOCS } from '@/content/help';
import { useViewport } from '@/lib/viewport';

type TabId = 'quickstart' | 'tasks' | 'docs';

export function HelpPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { role, setRole } = useScope();
  const { state, reset } = useOnboardingState();
  const { isMobile } = useViewport();
  const [tab, setTab] = useState<TabId>('quickstart');

  const content = useMemo(() => getOnboardingFor(role), [role]);
  const tasks = useMemo(() => getHelpTasksFor(role), [role]);

  const firstName = (user?.displayName || user?.email || 'bạn').split(' ').slice(-1)[0];

  function rerunOnboarding() {
    reset();
    navigate('/welcome');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Trợ giúp']}
        title="Trợ giúp & Hướng dẫn"
        subtitle={`Cá nhân hóa cho ${content.roleLabel} · ${firstName}`}
        actions={
          <Button variant="primary" size="sm" icon="play" onClick={rerunOnboarding}>
            Chạy lại hướng dẫn
          </Button>
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <Card padding={0} noBody style={{ marginBottom: 16 }}>
          <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
            <Icon name="shield" size={14} style={{ color: 'var(--bx-primary)' }} />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--bx-text-heading)' }}>
              Đổi vai trò để xem hướng dẫn khác
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {ROLE_OPTIONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 999,
                    border:
                      r.value === role
                        ? '1px solid var(--bx-primary)'
                        : '1px solid var(--bx-border)',
                    background: r.value === role ? 'var(--bx-primary)' : '#fff',
                    color: r.value === role ? '#fff' : 'var(--bx-text-body)',
                    fontSize: 12,
                    cursor: 'pointer',
                    fontWeight: r.value === role ? 600 : 500,
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {state.completedAt && (
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 11.5,
                  color: 'var(--bx-text-muted)',
                }}
              >
                Đã xem hướng dẫn lần cuối: {new Date(state.completedAt).toLocaleDateString('vi-VN')}
              </span>
            )}
          </div>
        </Card>

        <SectionTabs
          value={tab}
          onChange={(id) => setTab(id as TabId)}
          tabs={[
            { id: 'quickstart', label: 'Bắt đầu nhanh', idx: 1, count: content.steps.length },
            { id: 'tasks', label: 'Theo tác vụ', idx: 2, count: tasks.length },
            { id: 'docs', label: 'Tài liệu chi tiết', idx: 3, count: HELP_DOCS.length },
          ]}
        />

        <div style={{ marginTop: 16 }}>
          {tab === 'quickstart' && (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ fontSize: 13, color: 'var(--bx-text-muted)' }}>
                {content.tagline}
              </div>
              {content.steps.map((s, i) => (
                <Card key={s.id} padding={16}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <span
                      style={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        background: 'var(--bx-primary)',
                        color: '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {i + 1}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: 'var(--bx-text-heading)',
                          marginBottom: 4,
                        }}
                      >
                        {s.title}
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--bx-text-body)' }}>
                        {s.body}
                      </div>
                      {s.bullets && (
                        <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                          {s.bullets.map((b, j) => (
                            <li key={j} style={{ fontSize: 13, lineHeight: 1.55 }}>
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}
                      {s.ctaHref && s.ctaLabel && (
                        <div style={{ marginTop: 10 }}>
                          <Button
                            variant="ghost-primary"
                            size="sm"
                            iconRight="arrowRight"
                            onClick={() => navigate(s.ctaHref!)}
                          >
                            {s.ctaLabel}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {tab === 'tasks' && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: 12,
              }}
            >
              {tasks.map((t) => (
                <Card key={t.id} padding={14}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13.5,
                      color: 'var(--bx-text-heading)',
                      marginBottom: 4,
                    }}
                  >
                    {t.title}
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      lineHeight: 1.5,
                      color: 'var(--bx-text-muted)',
                      marginBottom: 10,
                    }}
                  >
                    {t.description}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconRight="arrowRight"
                    onClick={() => navigate(t.href)}
                  >
                    Mở
                  </Button>
                </Card>
              ))}
            </div>
          )}

          {tab === 'docs' && (
            <Card padding={0} noBody>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {HELP_DOCS.map((d, i) => (
                  <li
                    key={d.id}
                    style={{
                      borderTop: i === 0 ? 'none' : '1px solid var(--bx-border-muted)',
                    }}
                  >
                    <a
                      href={d.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 16px',
                        color: 'var(--bx-text-body)',
                        textDecoration: 'none',
                        fontSize: 13.5,
                      }}
                    >
                      <Icon name="info" size={13} style={{ color: 'var(--bx-text-muted)' }} />
                      <span style={{ flex: 1 }}>{d.title}</span>
                      <Icon name="external" size={12} style={{ color: 'var(--bx-text-muted)' }} />
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
